/**
 * evolutionChoice — the pure, headless brain behind the calm "press B to cancel" choice
 * at the evolving moment (CLAUDE.md: no pressure, nothing punishing — evolving is opt-in,
 * the level-up always stands). Shared by BOTH evolution flows: the Battle scene's
 * post-battle level-up pipeline and the Training Grounds' Rare-Candy bench.
 *
 * The scenes own only the rendering (a Menu + a DialogBox) and the side effects (swap the
 * evolved instance into the team, persist, the glow flourish). This module owns the bits
 * worth unit-testing without a Phaser/DOM scene: the two menu labels, the keyboard→cancel
 * mapping (Esc OR B = keep), and the result wording for each branch.
 */

/** The two rows of the no-pressure choice, in cursor order (Evolve first, Keep second). */
export interface EvolutionChoiceLabels {
  /** The confirming row — e.g. "Evolve into Ivysaur". */
  readonly evolve: string;
  /** The calm cancel row — keep the current form. */
  readonly keep: string;
}

/**
 * Build the two choice labels. `toName` is the species the mon would become; we name it on
 * the Evolve row so the player sees exactly what they're confirming, classic-style.
 */
export function evolutionChoiceLabels(toName: string): EvolutionChoiceLabels {
  return { evolve: `Evolve into ${toName}`, keep: 'Keep as is' };
}

/**
 * The keys that CANCEL (keep the current form) from the choice prompt: the classic "B"
 * (the owner's ask) and Esc, case-insensitively. Enter/Space/arrows are handled by the
 * Menu itself (confirm / move) and are intentionally NOT cancel keys here.
 */
export function isEvolutionCancelKey(key: string): boolean {
  return key === 'b' || key === 'B' || key === 'Escape';
}

/** The juicy payoff line when the player lets it evolve. */
export function evolvedMessage(fromName: string, toName: string): string {
  return `${fromName} evolved into ${toName}!`;
}

/** The calm, no-loss line when the player keeps the current form. The level-up still stood. */
export function stoppedEvolvingMessage(fromName: string): string {
  return `${fromName} stopped evolving.`;
}

/** The classic prelude line shown before the choice is offered. */
export function evolvingPromptMessage(fromName: string): string {
  return `What? ${fromName} is evolving!`;
}
